import launchSupport from 'owa-shared-start/lib/launchSupport';
import { applyStyles, createDiv, getMetadataDetails } from 'owa-shared-start/lib/errorPageHelpers';
import { getItem, itemExists, localStorageExists } from 'owa-local-storage';
import { isBootFeatureEnabled } from 'owa-metatags';
import type ErrorDiagnostics from 'owa-shared-start/lib/interfaces/ErrorDiagnostics';
import type { BootError, BootResult } from 'owa-shared-start-types';
import type NativeCallbacks from 'owa-shared-start/lib/interfaces/NativeCallbacks';

const BLUE_COLOR = '#0078D4';
const GREY_COLOR = '#333';
const FEWER_DETAILS_STRING = 'Fewer Details';
const SEMI_BOLD_FONT =
    "'Segoe UI WestEuropean', 'Segoe UI Semibold', 'Segoe WP Semibold', 'Segoe UI', 'Segoe WP', Tahoma, Arial, sans-serif'";

export default function handleErrorLocally(
    bootResult: BootResult,
    errorDiagnostics: ErrorDiagnostics,
    bootError: BootError,
    nativeCallbacks?: NativeCallbacks,
    bootErrorHandler?: () => boolean
) {
    const container = createDiv({
        position: 'relative',
        left: '5%',
        top: '100px',
    });

    // grab the logo from the loading screen. We will reuse this svg
    const logo = document.getElementById('loadingLogo');
    if (logo) {
        logo.querySelectorAll('*').forEach(item => {
            const logoElement = item as HTMLElement;
            const elementStyle = getComputedStyle(logoElement);

            // if the logo element has an animation then
            // the animation-duration is set to 0 to make it static
            if (elementStyle.animation) {
                applyStyles(logoElement, { 'animation-duration': '0s' });
            }
        });

        logo.remove();

        const logoStyles: {
            [style: string]: string;
        } = {
            position: 'initial',
            width: '56px',
            height: '56px',
            'margin-left': '-21px',
            transform: 'scale(0.25)',
        };
        applyStyles(logo, logoStyles);
        container.appendChild(logo);
    }

    // dismiss the loading screen so we can show the error page
    const loadingScreen = document.getElementById('loadingScreen');
    if (loadingScreen) {
        loadingScreen.remove();
    }

    if (isBootFeatureEnabled('supportonboot') && isSupportEnabled()) {
        // launch support instead
        try {
            launchSupport(
                bootResult,
                errorDiagnostics,
                bootError,
                nativeCallbacks,
                bootErrorHandler
            );
            return;
        } catch {} // Continue with the previous error page if something goes wrong
    }

    addDiv(
        container,
        { fontSize: '40px', color: BLUE_COLOR, 'margin-top': '28px' },
        'Something went wrong.'
    );
    addDiv(
        container,
        {
            fontSize: '16px',
            'font-family': SEMI_BOLD_FONT,
            margin: '20px 0',
            'font-weight': '600',
            color: GREY_COLOR,
        },
        'Please try again later.'
    );

    const span = document.createElement('span');
    applyStyles(span, {
        margin: '0 4px',
        'font-size': '14px',
        'line-height': '19px',
    });
    span.innerText = 'Refresh the page';
    const refresh = document.createElement('button');
    applyStyles(refresh, {
        color: 'white',
        'background-color': BLUE_COLOR,
        cursor: 'pointer',
        padding: '0 16px',
        'min-width': '80px',
        'border-radius': '2px',
        'min-height': '32px',
        border: '1px solid ' + BLUE_COLOR,
        'font-weight': '600',
        'font-family': SEMI_BOLD_FONT,
        'line-height': '19px',
    });
    refresh.appendChild(span);
    container.appendChild(refresh);

    const detailsDiv = addDiv(
        container,
        {
            fontSize: '14px',
            color: BLUE_COLOR,
            cursor: 'pointer',
            'font-family': SEMI_BOLD_FONT,
            'line-height': '19px',
            'font-weight': '600',
            margin: '20px 0',
        },
        FEWER_DETAILS_STRING
    );

    let details = getMetadataDetails();
    details.push('BootResult: ' + bootResult);
    if (bootError.request) {
        details.push('Failed resource: ' + bootError.request);
    }

    details = details.concat(
        (Object.keys(errorDiagnostics) as (keyof ErrorDiagnostics)[])
            .filter(k => errorDiagnostics[k])
            .map(k => k + ': ' + errorDiagnostics[k])
    );

    const errorContainer = document.createElement('pre');
    errorContainer.innerText = details.join('\n');
    applyStyles(errorContainer, {
        color: GREY_COLOR,
        'user-select': 'all',
        overflow: 'scroll',
        width: '100%',
    });

    container.appendChild(errorContainer);

    // Add click listeners
    detailsDiv.addEventListener('click', () => {
        const isFewerDetails = detailsDiv.innerText === FEWER_DETAILS_STRING;
        detailsDiv.innerText = isFewerDetails ? 'More Details' : FEWER_DETAILS_STRING;
        errorContainer.style.display = isFewerDetails ? 'none' : '';
    });

    refresh.addEventListener('click', () => {
        if (!bootErrorHandler?.()) {
            self.location.reload();
        }
    });

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion --
     * See aka.ms/mini-lint-errors
     *	> Forbidden non-null assertion. */
    document.getElementById('app')!.appendChild(container);
}

function addDiv(
    container: HTMLDivElement,
    styles: {
        [style: string]: string;
    },
    text?: string
) {
    const div = createDiv(styles);
    if (text) {
        div.innerText = text;
    }
    container.appendChild(div);
    return div;
}

function isSupportEnabled(): boolean {
    if (localStorageExists(window)) {
        const contactSupportPolicy = itemExists(window, 'ocpscontactsupportenabled')
            ? getItem(window, 'ocpscontactsupportenabled') === '1'
            : true;
        const diagnosticsTroubleshootingtPolicy = itemExists(
            window,
            'ocpsdiagnosticstroubleshootingenabled'
        )
            ? getItem(window, 'ocpsdiagnosticstroubleshootingenabled') === '1'
            : true;

        return contactSupportPolicy || diagnosticsTroubleshootingtPolicy;
    }

    return false;
}
